import { Link } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { apiFetch } from "api/Api";
import "assets/theme/admin_list.css";
import {
  FaGlobe,
  FaPlus,
  FaEdit,
  FaTrash,
  FaCheck,
  FaTimes,
  FaSearch,
  FaLayerGroup,
  FaBuilding,
} from "react-icons/fa";

const DistrictList = () => {
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [savingId, setSavingId] = useState(null);

  // Fetch district list
  const fetchDistricts = async () => {
    setLoading(true);
    try {
      const data = await apiFetch("super_admin/list_district");
      setDistricts(data.districts || data || []);
    } catch (err) {
      console.error("Error fetching districts:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDistricts();
  }, []);

  // Filtered districts based on search query
  const filteredDistricts = useMemo(() => {
    if (!searchQuery.trim()) return districts;
    const query = searchQuery.toLowerCase().trim();
    return districts.filter((d) =>
      d.district_name?.toLowerCase().includes(query)
    );
  }, [districts, searchQuery]);

  // Start inline editing
  const handleEdit = (item) => {
    setEditId(item.district_id);
    setEditName(item.district_name);
  };

  // Cancel inline editing
  const handleCancel = () => {
    setEditId(null);
    setEditName("");
  };

  // Save inline edit
  const handleSave = async (id) => {
    if (!editName.trim()) {
      alert("District name cannot be empty");
      return;
    }

    setSavingId(id);
    try {
      const data = await apiFetch(`super_admin/edit_district/${id}`, {
        method: "PUT",
        body: JSON.stringify({ district_name: editName.trim() }),
      });

      setDistricts((prev) =>
        prev.map((d) =>
          d.district_id === id ? { ...d, district_name: editName.trim() } : d
        )
      );

      handleCancel();
    } catch (error) {
      alert(error.message);
    } finally {
      setSavingId(null);
    }
  };

  // Delete district
  const handleDelete = async (id, name) => {
    if (
      !window.confirm(
        `Are you sure you want to delete district "${name}"?\nThis action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      const data = await apiFetch(`super_admin/delete_district/${id}`, {
        method: "DELETE",
      });

      setDistricts((prev) => prev.filter((d) => d.district_id !== id));
    } catch (error) {
      alert(error.message);
    }
  };

  // Helper for district initials avatar
  const getInitials = (name) => {
    if (!name) return "D";
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div className="admin-page-container">
        {/* Executive Header Card */}
        <div className="admin-header-card d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div className="d-flex align-items-center">
            <div className="admin-header-icon">
              <FaGlobe />
            </div>
            <div>
              <h4 className="admin-header-title">
                जिल्हा व्यवस्थापन / District Master
              </h4>
              <p className="admin-header-subtitle mb-0">
                सर्व जिल्ह्यांची यादी, जोडणे, संपादन आणि व्यवस्थापन
              </p>
            </div>
          </div>

          <Link to="/districts" className="btn-admin-add">
            <FaPlus /> <span>नवीन जिल्हा जोडा (Add District)</span>
          </Link>
        </div>

        {/* Filter & Search Toolbar Card */}
        <div className="admin-filter-card d-flex align-items-center justify-content-between flex-wrap gap-3">
          {/* Search Box */}
          <div className="admin-search-wrapper flex-grow-1" style={{ maxWidth: "420px" }}>
            <FaSearch className="admin-search-icon" />
            <input
              type="text"
              className="admin-search-input w-100"
              placeholder="जिल्हा शोधा... / Search District Name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Quick Metrics Badges */}
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center gap-2 px-3 py-2 bg-light rounded-3 border">
              <FaLayerGroup className="text-primary" />
              <span className="small text-muted fw-medium">एकूण (Total):</span>
              <span className="fw-bold text-dark">{districts.length}</span>
            </div>

            {searchQuery && (
              <div className="d-flex align-items-center gap-2 px-3 py-2 bg-light rounded-3 border">
                <span className="small text-muted fw-medium">आढळलेले (Found):</span>
                <span className="fw-bold text-success">{filteredDistricts.length}</span>
              </div>
            )}
          </div>
        </div>

        {/* Main Table Card */}
        <div className="admin-table-card shadow-sm">
          {loading ? (
            <div className="text-center py-5 text-muted">
              <div className="spinner-border text-primary mb-2" role="status" style={{ width: "2rem", height: "2rem" }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mb-0 fw-semibold small">लोड होत आहे... (Loading Districts)</p>
            </div>
          ) : filteredDistricts.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FaBuilding size={45} className="mb-3 opacity-40 text-secondary" />
              <h6 className="fw-bold text-dark mb-1">कोणताही जिल्हा आढळला नाही</h6>
              <p className="small text-muted mb-3">
                {searchQuery
                  ? `"${searchQuery}" नावाचा कोणताही जिल्हा सापडला नाही.`
                  : "यादीमध्ये कोणताही जिल्हा उपलब्ध नाही. नवीन जिल्हा जोडण्यासाठी खालील बटण वापरा."}
              </p>
              {!searchQuery && (
                <Link to="/districts" className="btn btn-primary btn-sm rounded-pill px-4">
                  <FaPlus className="me-1" /> नवीन जिल्हा जोडा
                </Link>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-admin-custom align-middle">
                <thead>
                  <tr>
                    <th style={{ width: "70px" }}>अ.क्र. (Sr. No.)</th>
                    <th>जिल्हा नाव (District Name)</th>
                    <th style={{ width: "160px" }} className="text-center">स्थिती (Status)</th>
                    <th style={{ width: "150px" }} className="text-end">
                      कृती (Actions)
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredDistricts.map((item, index) => (
                    <tr key={item.district_id}>
                      {/* Serial Number */}
                      <td className="fw-bold text-secondary">{index + 1}</td>

                      {/* District Name with Avatar Chip */}
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="admin-avatar-chip flex-shrink-0"
                            style={{
                              background: "linear-gradient(135deg, #0f172a 0%, #2563eb 100%)",
                            }}
                          >
                            {getInitials(item.district_name)}
                          </div>

                          <div className="flex-grow-1">
                            {editId === item.district_id ? (
                              <input
                                type="text"
                                className="form-control form-control-sm admin-select py-1 px-3"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                placeholder="District Name"
                                autoFocus
                              />
                            ) : (
                              <span className="fw-bold text-dark text-capitalize" style={{ fontSize: "0.95rem" }}>
                                {item.district_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Active Status Badge */}
                      <td className="text-center">
                        <span className="badge bg-success-subtle text-success border border-success-subtle px-3 py-1 rounded-pill fw-semibold">
                          सक्रिय (Active)
                        </span>
                      </td>

                      {/* Action Controls */}
                      <td className="text-end">
                        {editId === item.district_id ? (
                          <div className="admin-action-group justify-content-end">
                            <button
                              className="btn-action-icon btn-action-view"
                              onClick={() => handleSave(item.district_id)}
                              disabled={savingId === item.district_id}
                              title="जतन करा (Save)"
                            >
                              <FaCheck />
                            </button>

                            <button
                              className="btn-action-icon btn-action-delete"
                              onClick={handleCancel}
                              disabled={savingId === item.district_id}
                              title="रद्द करा (Cancel)"
                            >
                              <FaTimes />
                            </button>
                          </div>
                        ) : (
                          <div className="admin-action-group justify-content-end">
                            <button
                              className="btn-action-icon btn-action-edit"
                              onClick={() => handleEdit(item)}
                              title="संपादित करा (Edit District)"
                            >
                              <FaEdit />
                            </button>

                            <button
                              className="btn-action-icon btn-action-delete"
                              onClick={() => handleDelete(item.district_id, item.district_name)}
                              title="हटवा (Delete District)"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DistrictList;
